import { ReactElement, useMemo, useState } from 'react';
import { Device } from '@/api';
import {
    UIAvatar,
    UIButton,
    UIFormInput,
    UIInlineAlert,
    UISection,
    UISectionContent,
    UISpacer,
    UISpinner,
    UIText
} from '@/ui';
import { useDevicePermissions, useDevicesTranslation } from '../hooks';
import { useFetchDevice } from '../queries';
import { AssignDeviceModal } from './assign-device-modal';
import { DeviceFeaturesIcons } from './device-features-icons';
import { DeviceIdLabel } from './device-id-label';
import { DeviceStatus } from './device-status';
import { UpdateDeviceModal } from './update-device-modal';

export type DeviceDetailProps = {
    deviceId: string;
};

export function DeviceDetail(props: DeviceDetailProps): ReactElement {
    const { deviceId } = props;
    const { t } = useDevicesTranslation();

    const { device, status, error, canRetry, refetch } =
        useFetchDevice(deviceId);

    if (status === 'pending') {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}
            >
                <UISpinner />
            </div>
        );
    }

    if (status === 'error') {
        return (
            <>
                <UIInlineAlert text={error} />
                {canRetry && (
                    <>
                        <UISpacer />
                        <UIButton
                            onClick={refetch}
                            disabled={!canRetry}
                            variant="primary"
                        >
                            {t('shared.labels.retry')}
                        </UIButton>
                    </>
                )}
            </>
        );
    }

    if (device) {
        return <LoadedDeviceDetail device={device} />;
    }

    return <></>;
}

type DeviceDetailLoadedProps = {
    device: Device;
};
function LoadedDeviceDetail(props: DeviceDetailLoadedProps): ReactElement {
    const { device } = props;
    const { t } = useDevicesTranslation();

    // Scoped to the device's company, not the selected one — this page is the
    // QR-scan landing page and sits outside the company context, so the device
    // may belong to any company the user is in.
    const { canEditDevices: canEdit } = useDevicePermissions({
        companyId: device.companyId ?? ''
    });

    const teamName = useMemo(() => {
        if (device.team) {
            return `${device.team.name}`;
        } else {
            return t('components.device_detail.no_team');
        }
    }, [device.team, t]);

    const userName = useMemo(() => {
        if (device.user) {
            if (device.user.joined) {
                return `${device.user.firstName} ${device.user.lastName}`;
            } else {
                return `${device.user.email}`;
            }
        } else {
            return t('components.device_detail.no_member');
        }
    }, [device.user, t]);

    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

    return (
        <>
            <div style={{ display: 'flex', gap: 16, flexDirection: 'column' }}>
                <UISection>
                    <UISectionContent>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 24
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 16
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flex: 1,
                                        gap: 16,
                                        alignItems: 'center'
                                    }}
                                >
                                    <UIAvatar
                                        size={64}
                                        img={device.image}
                                        alt=""
                                    />
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 4
                                        }}
                                    >
                                        <UIText bold>{device.name}</UIText>
                                        <DeviceIdLabel deviceId={device.id} />
                                    </div>
                                </div>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 16
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flex: 1
                                    }}
                                >
                                    <UIText>
                                        {t('shared.fields.team.label')}
                                    </UIText>
                                </div>

                                <div
                                    style={{
                                        display: 'flex',
                                        flex: 2
                                    }}
                                >
                                    <UIFormInput
                                        name="Team"
                                        value={teamName}
                                        disabled
                                    />
                                </div>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 16
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flex: 1
                                    }}
                                >
                                    <UIText>
                                        {t(
                                            'components.device_detail.member_label'
                                        )}
                                    </UIText>
                                </div>

                                <div
                                    style={{
                                        display: 'flex',
                                        flex: 2
                                    }}
                                >
                                    <UIFormInput
                                        name="User"
                                        value={userName}
                                        disabled
                                    />
                                </div>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 16
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flex: 1
                                    }}
                                >
                                    <UIText>
                                        {t('shared.devices.features_label')}
                                    </UIText>
                                </div>

                                <div
                                    style={{
                                        display: 'flex',
                                        flex: 2
                                    }}
                                >
                                    <DeviceFeaturesIcons device={device} />
                                </div>
                            </div>
                            {device.battery !== undefined && (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gap: 16
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            flex: 1
                                        }}
                                    >
                                        <UIText>
                                            {t('shared.labels.status')}
                                        </UIText>
                                    </div>

                                    <div
                                        style={{
                                            display: 'flex',
                                            flex: 2
                                        }}
                                    >
                                        <DeviceStatus
                                            device={device}
                                            showBattery={true}
                                            showLocation={false}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </UISectionContent>
                </UISection>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        gap: 8
                    }}
                >
                    {canEdit && (
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column'
                            }}
                        >
                            <UIButton
                                onClick={() => {
                                    setIsUpdateModalOpen(true);
                                }}
                            >
                                {t('shared.labels.edit')}
                            </UIButton>
                        </div>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            flex: 1,
                            flexDirection: 'column'
                        }}
                    >
                        <UIButton
                            onClick={() => {
                                setIsAssignModalOpen(true);
                            }}
                            variant="primary"
                        >
                            {t('components.device_detail.assign_to_self')}
                        </UIButton>
                    </div>
                </div>
            </div>
            {/* Mounted only while open: the modal's team list is company-scoped
                work that this page has no reason to do until it is asked for. */}
            {isUpdateModalOpen && (
                <UpdateDeviceModal
                    device={device}
                    isOpen={isUpdateModalOpen}
                    onClose={() => setIsUpdateModalOpen(false)}
                />
            )}
            <AssignDeviceModal
                device={device}
                isOpen={isAssignModalOpen}
                onClose={() => setIsAssignModalOpen(false)}
            />
        </>
    );
}
